import { Link, SpaceBetween, TextContent } from '@amzn/awsui-components-react';
import React from 'react';
import { useIntl } from 'react-intl';
import { GandalfUser } from '../types/auth';
import { AppConfig } from '../types/app';
import identityConsentMessages from '../i18n/identityConsent.messages';
import { T_AND_C_PRIVACY, T_AND_C_TERMS } from '../constants/links';
import { EULA_CONFIRMATION } from '../constants/auth';
import eulaMessages from '../i18n/eula.messages';
import { shouldAutoAcceptEula } from './postAuth/EulaCheck';

interface EulaAndConsent {
  isEULAPresent: boolean;
  isConsentRequired: boolean;
}

const IdentityConsentInfo = ({
  user,
  config,
  idpType,
}: {
  user: GandalfUser;
  config?: AppConfig;
  idpType?: string;
}) => {
  const { formatMessage } = useIntl();

  const eulaAndConsent = checkConsentOrEULAOnFile(user, config, idpType);

  return (
    <SpaceBetween direction="vertical" size="m">
      <TextContent>
        <h2>{formatMessage(getConsentHeader(eulaAndConsent))}</h2>
      </TextContent>
      <TextContent>
        {formatMessage(getConsentContent(eulaAndConsent), {
          br: <br />,
          b: (str) => <b>{str}</b>,
          tandcLink: (str) => (
            <Link
              href={T_AND_C_TERMS}
              target="_blank"
              rel="noopener noreferrer"
              variant="primary"
              ariaLabel={formatMessage(
                eulaMessages.termsAndConditionsAndOrganizationAriaLabel
              )}
            >
              {str}
            </Link>
          ),
          privacyLink: (str) => (
            <Link
              href={T_AND_C_PRIVACY}
              target="_blank"
              rel="noopener noreferrer"
              variant="primary"
              ariaLabel={formatMessage(
                eulaMessages.privacyNoticeAndOrganizationAriaLabel
              )}
            >
              {str}
            </Link>
          ),
          orgName: user.invitation?.organization_name,
        })}
      </TextContent>
    </SpaceBetween>
  );
};

const checkConsentOrEULAOnFile = (
  user: GandalfUser,
  config?: AppConfig,
  idpType?: string
) => {
  const eulaAndConsent: EulaAndConsent = {
    isEULAPresent: false,
    isConsentRequired: false,
  };

  // EULA is considered "present" (so we show the Org-Consent-only screen rather
  // than the combined Terms & Conditions + Org Consent screen) when either:
  //   1. the EULA is already accepted on the user's token, OR
  //   2. the EULA will be silently auto-accepted for this simplified sign-in
  //      flow (non-ESSO/ELTI). In the auto-accept case the terms are surfaced
  //      inline on the simplified sign-in page, and EulaCheck accepts the EULA
  //      via API AFTER this consent screen, so user.eulaStatus is still 'false'
  //      here. Without this, the combined T&C+Org-Consent screen would be shown
  //      even though the user should only need to consent to the organization.
  if (
    user.eulaStatus?.toLowerCase() === EULA_CONFIRMATION ||
    (config ? shouldAutoAcceptEula(config, idpType) : false)
  ) {
    eulaAndConsent.isEULAPresent = true;
  }

  if (user.invitation?.learner_data_consent_required === true) {
    eulaAndConsent.isConsentRequired = true;
  }

  return eulaAndConsent;
};

const getConsentHeader = (eulaAndConsent: EulaAndConsent) => {
  if (eulaAndConsent.isEULAPresent && eulaAndConsent.isConsentRequired) {
    return identityConsentMessages.organizationDataAccessNoticeHeader;
  } else {
    return identityConsentMessages.termsAndConditionsAndOrganizationDataAccessNoticeHeader;
  }
};

const getConsentContent = (eulaAndConsent: EulaAndConsent) => {
  if (eulaAndConsent.isEULAPresent && eulaAndConsent.isConsentRequired) {
    return identityConsentMessages.organizationDataAccessNoticeBody;
  } else {
    return identityConsentMessages.termsAndConditionsAndOrganizationDataAccessNoticeBody;
  }
};

export default IdentityConsentInfo;
